// src/components/pages/Export.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { Download, Filter, X, Eye, ChevronUp, ChevronDown, Info, BarChart3 } from 'lucide-react';
import { fetchExportData } from '../../services/api';
import * as XLSX from 'xlsx';
import DetailedDeliveryNoteModal from '../export/DetailedDeliveryNoteModal';
import MasseNettoChart from '../export/MasseNettoChart';

const calculateEmissionsfaktor = (mischanlage, material) => {
  // Handle undefined or null inputs
  if (!mischanlage || !material) {
    return 0;
  }

  // Normalize inputs to handle case variations
  const normalizedMischanlage = mischanlage.toString().toLowerCase();
  const normalizedMaterial = material.toString().toLowerCase();

  // Sinsheim mapping (keep existing logic)
  if (normalizedMischanlage.includes('sinsheim') && normalizedMaterial.includes('ac 11 d sp')) {
    return 47;
  }

  // RM Asphalt Büttelborn mappings
  if (normalizedMischanlage.includes('büttelborn')) {
    if (normalizedMaterial.includes('ac 11 d sp')) return 39;
    if (normalizedMaterial.includes('ac 22 b s')) return 31;
    if (normalizedMaterial.includes('ac 22 t s')) return 28;
  }

  // DA Ludwigshafen mappings
  if (normalizedMischanlage.includes('ludwigshafen')) {
    if (normalizedMaterial.includes('ac 11 d sp')) return 54;
    if (normalizedMaterial.includes('ac 22 b s')) return 49;
  }

  // Return 0 if no match found
  return 0;
};

const calculateGWP = (emissionsfaktor, masseNetto) => {
  return emissionsfaktor * masseNetto /1000;
};


const exportToExcel = (data) => {
  if (!data || data.length === 0) {
    console.error('No data to export');
    return;
  }

  const columns = [
    // Only the fields specifically requested
    'Zeitstempel',
    'Lieferscheinnummer',
    'Asphaltmischgut',
    'Netto Gewicht',
    'Brutto Gewicht',
    'Tara Gewicht',
    'Mischanlage Name',
    'Mischanlage PLZ',
    'Mischanlage Stadt',
    'Mischanlage Adresse',
    'Wiegung Tara',
    'Wiegung Brutto',
    'Fahrzeugtyp',
    'Fahrzeug Kennzeichen',
    'Emissionsfaktor',
    'GWP'
  ];

  const colWidths = [
    // Only the fields specifically requested
    { wch: 20 }, // Zeitstempel
    { wch: 15 }, // Lieferscheinnummer
    { wch: 30 }, // Asphaltmischgut
    { wch: 15 }, // Netto Gewicht
    { wch: 15 }, // Brutto Gewicht
    { wch: 15 }, // Tara Gewicht
    { wch: 30 }, // Mischanlage Name
    { wch: 10 }, // Mischanlage PLZ
    { wch: 20 }, // Mischanlage Stadt
    { wch: 30 }, // Mischanlage Adresse
    { wch: 20 }, // Wiegung Tara
    { wch: 20 }, // Wiegung Brutto
    { wch: 15 }, // Fahrzeugtyp
    { wch: 20 }, // Fahrzeug Kennzeichen
    { wch: 15 }, // Emissionsfaktor
    { wch: 15 }  // GWP
  ];

  // Create a new workbook
  const wb = XLSX.utils.book_new();

  // Keep track of used sheet names to avoid duplicates
  const usedSheetNames = new Set();

  // Process each file (project) separately
  data.forEach((file, index) => {
    if (!file.pages || file.pages.length === 0) return;

    // Use the filename as the sheet name (clean it up for Excel compatibility)
    let baseSheetName = file.filename
      .replace(/[\[\]\*\/\\?:]/g, '') // Remove characters not allowed in Excel sheet names
      .substring(0, 31); // Excel sheet names are limited to 31 characters

    // If the filename starts with a number, prepend 'P_' to make it valid
    if (/^\d/.test(baseSheetName)) {
      baseSheetName = 'P_' + baseSheetName;
    }

    // Ensure unique sheet name
    let sheetName = baseSheetName;
    let counter = 1;
    while (usedSheetNames.has(sheetName)) {
      // If name is taken, append a number (keeping within 31 char limit)
      const suffix = `_${counter}`;
      const maxBaseLength = 31 - suffix.length;
      sheetName = baseSheetName.substring(0, maxBaseLength) + suffix;
      counter++;
    }
    usedSheetNames.add(sheetName);

    // Process the data for this project
    let excelData = file.pages.map(row => {
      const masseNetto = row['Masse Netto'] ? Number(row['Masse Netto']) : 0;
      const emissionsfaktor = row.Emissionsfaktor || 0;
      const gwp = calculateGWP(emissionsfaktor, masseNetto);

      // Get the original data which contains all the detailed information
      const originalData = row.originalData || {};
      const standardData = originalData.standard || {};
      const documentInfo = standardData.documentInfo || {};

      // Store the original timestamp for sorting
      const originalTimestamp = documentInfo.timeStamp || row.originalTimestamp;

      // Helper function to format datetime consistently
      const formatDateTime = (isoString) => {
        if (!isoString) return '-';
        try {
          const date = new Date(isoString);
          const day = date.getDate().toString().padStart(2, '0');
          const month = (date.getMonth() + 1).toString().padStart(2, '0');
          const year = date.getFullYear();
          const hours = date.getHours().toString().padStart(2, '0');
          const minutes = date.getMinutes().toString().padStart(2, '0');

          return `${day}.${month}.${year} ${hours}:${minutes}`;
        } catch (e) {
          return '-';
        }
      };

      // Helper function to format weight as number (without unit for Excel)
      const formatWeightAsNumber = (quantity) => {
        if (!quantity?.value) return null;
        return parseFloat(quantity.value);
      };

      // Only include the fields that were specifically requested
      return {
        // Fields in the specified order
        Zeitstempel: documentInfo.timeStamp ? formatDateTime(documentInfo.timeStamp) : row.Zeitstempel || '-',
        Lieferscheinnummer: documentInfo.documentNumber || row.Lieferscheinnummer || '-',
        Asphaltmischgut: standardData.material?.asphaltMixture || row.Material || '-',
        'Netto Gewicht': formatWeightAsNumber(standardData.material?.quantities?.netto) ?? (masseNetto ? parseFloat(masseNetto) : null),
        'Brutto Gewicht': formatWeightAsNumber(standardData.material?.quantities?.brutto),
        'Tara Gewicht': formatWeightAsNumber(standardData.material?.quantities?.tara),
        'Mischanlage Name': standardData.productionPlant?.name || row.Mischanlage || '-',
        'Mischanlage PLZ': standardData.productionPlant?.location?.postalCode || '-',
        'Mischanlage Stadt': standardData.productionPlant?.location?.city || '-',
        'Mischanlage Adresse': standardData.productionPlant?.location?.address || '-',
        'Wiegung Tara': formatDateTime(standardData.transport?.weighing?.tare) || '-',
        'Wiegung Brutto': formatDateTime(standardData.transport?.weighing?.gross) || '-',
        'Fahrzeugtyp': standardData.transport?.vehicle?.type || '-',
        'Fahrzeug Kennzeichen': standardData.transport?.vehicle?.FahrzeugKennzeichen || '-',
        Emissionsfaktor: emissionsfaktor,
        GWP: gwp,
        // Hidden field for sorting
        _originalTimestamp: originalTimestamp
      };
    });

    // Sort by timestamp (oldest first)
    excelData.sort((a, b) => {
      const timestampA = a._originalTimestamp ? new Date(a._originalTimestamp).getTime() : 0;
      const timestampB = b._originalTimestamp ? new Date(b._originalTimestamp).getTime() : 0;
      return timestampA - timestampB; // Ascending order (oldest first)
    });

    // Remove the hidden sorting field before export
    excelData = excelData.map(item => {
      const { _originalTimestamp, ...rest } = item;
      return rest;
    });

    // Create a worksheet for this project
    const ws = XLSX.utils.json_to_sheet(excelData, {
      header: columns
    });

    // Set number format for numeric columns
    const range = XLSX.utils.decode_range(ws['!ref']);
    const numericColumns = ['Netto Gewicht', 'Brutto Gewicht', 'Tara Gewicht', 'Emissionsfaktor', 'GWP'];
    numericColumns.forEach(colName => {
      const colIndex = columns.indexOf(colName);
      if (colIndex !== -1) {
        for (let row = range.s.r + 1; row <= range.e.r; row++) {
          const cell = XLSX.utils.encode_cell({ r: row, c: colIndex });
          if (ws[cell]) {
            if (typeof ws[cell].v === 'number') {
              ws[cell].z = '#,##0.00';
            } else if (ws[cell].v === null || ws[cell].v === undefined) {
              // Handle null/undefined values for weight columns
              ws[cell].v = '';
              ws[cell].t = 's'; // Set as string type for empty cells
            }
          }
        }
      }
    });

    // Set column widths
    ws['!cols'] = colWidths;

    // Add the worksheet to the workbook
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
  });

  // If no sheets were added (no valid data), add an empty sheet
  if (wb.SheetNames.length === 0) {
    const ws = XLSX.utils.aoa_to_sheet([['No data available']]);
    XLSX.utils.book_append_sheet(wb, ws, 'No Data');
  }

  const fileName = `export_${new Date().toISOString().slice(0,10)}.xlsx`;
  XLSX.writeFile(wb, fileName);
};

const SortButton = ({ column, sortConfig, onSort }) => {
  return (
    <button
      onClick={() => onSort(column)}
      className="inline-flex flex-col hover:opacity-80 transition-opacity"
    >
      <ChevronUp
        className={`h-4 w-4 ${sortConfig?.field === column && sortConfig?.direction === 'asc' ? 'text-green-600' : 'text-green-500'}`}
      />
      <ChevronDown
        className={`h-4 w-4 ${sortConfig?.field === column && sortConfig?.direction === 'desc' ? 'text-green-600' : 'text-green-500'}`}
      />
    </button>
  );
};

const DataTable = ({ isOpen, onClose, data, onRefresh }) => {
  const [sortConfig, setSortConfig] = useState(null);
  const [globalSearch, setGlobalSearch] = useState('');
  const [selectedDeliveryNote, setSelectedDeliveryNote] = useState(null);
  const [showChart, setShowChart] = useState(false);

  const handleSort = (field) => {
    let direction = 'asc';
    if (sortConfig?.field === field && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ field, direction });
  };

  const handleGlobalSearch = (value) => {
    setGlobalSearch(value);
  };

  const toggleChartView = () => {
    setShowChart(!showChart);
  };

  const filteredAndSortedData = useMemo(() => {
    if (!data.pages) return [];

    let result = [...data.pages];

    // Global search across all columns
    if (globalSearch) {
      const searchTerm = globalSearch.toLowerCase();
      result = result.filter(item => {
        // Search across all visible columns
        const searchableFields = [
          'Lieferscheinnummer',
          'Zeitstempel',
          'Mischanlage',
          'Material',
          'Masse Netto',
          'Emissionsfaktor',
          'GWP'
        ];

        return searchableFields.some(field => {
          const value = String(item[field] || '').toLowerCase();
          return value.includes(searchTerm);
        });
      });
    }

    if (sortConfig) {
      result.sort((a, b) => {
        let aValue = a[sortConfig.field];
        let bValue = b[sortConfig.field];

        if (['Masse Netto', 'Emissionsfaktor', 'GWP'].includes(sortConfig.field)) {
          aValue = parseFloat(aValue);
          bValue = parseFloat(bValue);
        }

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [data.pages, sortConfig, globalSearch]);

  if (!isOpen) return null;

  const columns = [
    { field: 'Lieferscheinnummer', label: 'LIEFERSCHEIN', width: '170px' },
    { field: 'Zeitstempel', label: 'ZEITSTEMPEL', width: '170px' },
    { field: 'Mischanlage', label: 'MISCHANLAGE', width: '220px' },
    { field: 'Material', label: 'MATERIAL', width: '220px' },
    { field: 'Masse Netto', label: 'MASSE NETTO', width: '120px' },
    { field: 'Emissionsfaktor', label: 'EMISSIONSFAKTOR', width: '150px' },
    { field: 'GWP', label: 'GWP', width: '120px' }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-[1400px] max-h-[85vh] min-h-[50vh] flex flex-col overflow-hidden">
        <div className="flex justify-between items-center p-4 border-b">
          <div className="flex items-center">
            <h2 className="text-xl font-semibold">Details für {data.filename}</h2>
            <button
              onClick={toggleChartView}
              className="ml-3 text-green-500 hover:text-green-700 transition-colors flex items-center"
              title="MASSE NETTO über Zeit anzeigen"
            >
              <BarChart3 className="h-5 w-5" />
            </button>
          </div>
          <div className="flex items-center gap-4">
            {!showChart && (
              <div className="relative">
                <input
                  type="text"
                  placeholder="Suchen..."
                  className="w-64 px-3 py-2 text-sm bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
                  onChange={(e) => handleGlobalSearch(e.target.value)}
                  value={globalSearch}
                />
                {globalSearch && (
                  <button
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    onClick={() => handleGlobalSearch('')}
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            )}
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {!showChart ? (
          <>
            <div className="border-b">
              <div className="w-full inline-block align-middle">
                <table className="w-full table-fixed">
                  <thead>
                    <tr>
                      {columns.map(column => (
                        <th
                          key={column.field}
                          className="p-4 border-b text-center"
                          style={{ width: column.width }}
                        >
                          <div className="flex items-center justify-center gap-2 whitespace-nowrap">
                            <span className="text-sm font-semibold text-black">
                              {column.label}
                            </span>
                            <SortButton
                              column={column.field}
                              sortConfig={sortConfig}
                              onSort={handleSort}
                            />
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                </table>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              <div className="w-full inline-block align-middle">
                <table className="w-full table-fixed">
                  <tbody>
                  {filteredAndSortedData.map((item, index) => {
                    const masseNetto = parseFloat(item['Masse Netto'] || item.Masse_Netto) || 0;
                    const emissionsfaktor = item.Emissionsfaktor || 0;  // Use the value from the backend
                    const gwp = calculateGWP(emissionsfaktor, masseNetto);

                    return (
                      <tr key={index} className="border-b hover:bg-gray-50">
                        <td className="p-4 text-center" style={{ width: columns[0].width }}>
                          <div className="flex items-center gap-2 justify-center">
                            {item.Lieferscheinnummer}
                            <button
                              onClick={() => setSelectedDeliveryNote(item.originalData)}
                              className="text-blue-500 hover:text-blue-700"
                            >
                              <Info className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                        <td className="p-4 text-center" style={{ width: columns[1].width }}>{item.Zeitstempel}</td>
                        <td className="p-4 text-center" style={{ width: columns[2].width }}>{item.Mischanlage}</td>
                        <td className="p-4 text-center" style={{ width: columns[3].width }}>{item.Material}</td>
                        <td className="p-4 text-center" style={{ width: columns[4].width }}>{masseNetto.toFixed(2)} t</td>
                        <td className="p-4 text-center" style={{ width: columns[5].width }}>{emissionsfaktor}</td>
                        <td className="p-4 text-center" style={{ width: columns[6].width }}>{gwp.toFixed(2)}</td>
                      </tr>
                    );
                  })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 p-6 overflow-hidden">
            <MasseNettoChart
              data={data}
              onBack={toggleChartView}
            />
          </div>
        )}
      </div>

      <DetailedDeliveryNoteModal
        isOpen={!!selectedDeliveryNote}
        onClose={() => setSelectedDeliveryNote(null)}
        data={selectedDeliveryNote}
        onRefresh={onRefresh}
      />
    </div>
  );
};

const Export = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  // Helper function to format timeframe like in analytics page
  const formatTimeframe = (timeframe) => {
    if (!timeframe || !timeframe.start || !timeframe.end) {
      return "Nicht verfügbar";
    }

    try {
      const startDate = new Date(timeframe.start);
      const endDate = new Date(timeframe.end);

      // Format as DD.MM.YY
      const formatDate = (date) => {
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear().toString().slice(-2); // Get last 2 digits of year
        return `${day}.${month}.${year}`;
      };

      return `${formatDate(startDate)}-${formatDate(endDate)}`;
    } catch (error) {
      console.error('Error formatting timeframe:', error);
      return "Nicht verfügbar";
    }
  };

  // Helper function to format asphalt quantity with German formatting
  const formatAsphaltQuantity = (quantity) => {
    if (!quantity || quantity === 0) {
      return "0,0 t";
    }

    try {
      // Round to one decimal place like in analytics
      const roundedQuantity = Math.round(quantity * 10) / 10;

      // Format with German number formatting (thousands separator and comma for decimal)
      const formattedNumber = roundedQuantity.toLocaleString('de-DE', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
      });

      return `${formattedNumber} t`;
    } catch (error) {
      console.error('Error formatting asphalt quantity:', error);
      return "0,0 t";
    }
  };

  // Helper function to format CO₂e emissions with German formatting
  const formatCO2Emissions = (emissions) => {
    if (!emissions || emissions === 0) {
      return "0,0 t";
    }

    try {
      // Round to one decimal place like in analytics
      const roundedEmissions = Math.round(emissions * 10) / 10;

      // Format with German number formatting (thousands separator and comma for decimal)
      const formattedNumber = roundedEmissions.toLocaleString('de-DE', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
      });

      return `${formattedNumber} t`;
    } catch (error) {
      console.error('Error formatting CO2 emissions:', error);
      return "0,0 t";
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await fetchExportData();

      // Sort projects by starting date of PROJEKTZEITRAUM (oldest first)
      const sortedData = [...response.data].sort((a, b) => {
        const dateA = a.timeframe?.start ? new Date(a.timeframe.start) : new Date(0);
        const dateB = b.timeframe?.start ? new Date(b.timeframe.start) : new Date(0);
        return dateA - dateB; // Ascending order (oldest first)
      });

      setData(sortedData);
      setLoading(false);
    } catch (err) {
      setError('Failed to load export data');
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-600">{error}</div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-semibold">Datenexport</h1>
      <p className="text-gray-500 mt-1">Alle Lieferscheine und Analysedaten</p>

      <div className="mt-8 flex items-center gap-2">
        <button
          onClick={() => exportToExcel(data)}
          className="inline-flex items-center gap-2 bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600"
        >
          <Download className="h-4 w-4" />
          Exportieren
        </button>
        <button className="inline-flex items-center gap-2 text-gray-600 px-4 py-2 rounded-md hover:bg-gray-100">
          <Filter className="h-4 w-4" />
          Filter
        </button>
      </div>

      <div className="mt-6">
        <table className="w-full">
          <thead>
            <tr className="border-b">
              <th className="text-left pb-4 text-xs font-medium text-gray-500">PROJEKT</th>
              <th className="text-left pb-4 text-xs font-medium text-gray-500">PROJEKTZEITRAUM</th>
              <th className="text-left pb-4 text-xs font-medium text-gray-500">LIEFERSCHEINE</th>
              <th className="text-left pb-4 text-xs font-medium text-gray-500">MENGE ASPHALTMISCHGUT</th>
              <th className="text-left pb-4 text-xs font-medium text-gray-500">CO₂e EMISSIONEN</th>
              <th className="text-left pb-4 text-xs font-medium text-gray-500">DETAILS</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item) => (
              <tr key={item.id} className="border-b hover:bg-gray-50">
                <td className="py-4">{item.filename}</td>
                <td className="py-4">
                  {formatTimeframe(item.timeframe)}
                </td>
                <td className="py-4">{item.total_pages_processed}</td>
                <td className="py-4">{formatAsphaltQuantity(item.total_asphalt_quantity)}</td>
                <td className="py-4">{formatCO2Emissions(item.total_co2_emissions)}</td>
                <td className="py-4">
                  <button
                    onClick={() => setSelectedFile(item)}
                    className="text-blue-500 hover:text-blue-700 inline-flex items-center gap-1"
                  >
                    <Eye className="h-4 w-4" />
                    Anzeigen
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <DataTable
        isOpen={!!selectedFile}
        onClose={() => setSelectedFile(null)}
        data={selectedFile || {}}
        onRefresh={loadData}
      />
    </div>
  );
};

export default Export;